/**
 * 풀이 모드 — 레벨·중심 리듬에 맞는 화성진행 + 베이스/소프라노 선율 생성.
 *
 * 1) generateHarmonyProgression: 슬롯마다 로마숫자 화음 (중심 리듬 = 화음 길이)
 * 2) bass/sopranoNotesFromProgression: 단성 문제용
 * 3) generateOuterVoicesWithProgression: 소프라노+베이스 동시 생성 (외성부 성부법 준수)
 *
 * 장조 12키. 레벨 누적은 solveLevels.js unlocks 기준.
 */
import { BEAT_VALUES, getBeatsPerMeasure, getVoiceRanges, midiToPitch, pitchToMidi } from './musicTheory.js'
import { getTheoryRules } from './harmonyEngine.js'
import { harmonySpanBeats, unlocksUpToLevel, focusFamiliesForLevel, DEFAULT_SOLVE_LEVEL, normalizeSolveLevelId } from './solveLevels.js'

/** 해당 패밀리가 이 레벨의 "중심 소재"면 boosted, 그 외(누적 배경)면 base 확률을 쓴다. */
function chance(family, focus, base, boosted) {
  return focus.has(family) ? boosted : base
}

const KEY_TO_TONIC_PC = { C: 0, G: 7, D: 2, A: 9, E: 4, B: 11, 'F#': 6, F: 5, Bb: 10, Eb: 3, Ab: 8, Db: 1 }
const PC_TO_KEY = Object.fromEntries(Object.entries(KEY_TO_TONIC_PC).map(([k, v]) => [v, k]))
/** 딸림조(5도 위) 키 이름 — 전조(1편 8장)에서 사용 */
function dominantKeyOf(key) {
  return PC_TO_KEY[(KEY_TO_TONIC_PC[key] + 7) % 12]
}

/** 버금딸림조(5도 아래, 4도 위) 키 이름 — 전조(1편 8장)에서 사용 */
function subdominantKeyOf(key) {
  return PC_TO_KEY[(KEY_TO_TONIC_PC[key] + 5) % 12]
}

/**
 * oldKey·newKey 둘 다에서 온음계적 3화음으로 성립하는 공통화음(피벗) 후보.
 * 감3화음(7도 화음)은 공통화음으로 잘 안 쓰인다는 원서 설명에 따라 제외.
 * @returns {Array<{ oldDeg: number, newDeg: number }>}
 */
function pivotChordCandidates(oldKey, newKey) {
  const out = []
  for (let oldDeg = 0; oldDeg < 7; oldDeg++) {
    if (oldDeg === 6) continue
    const oldQuality = MAJOR_DEGREE_QUALITY[oldDeg]
    const rootPc = degreeChordTones(oldKey, oldDeg).tones.root.pc
    for (let newDeg = 0; newDeg < 7; newDeg++) {
      if (newDeg === 6) continue
      if (MAJOR_DEGREE_QUALITY[newDeg] !== oldQuality) continue
      if (degreeChordTones(newKey, newDeg).tones.root.pc !== rootPc) continue
      out.push({ oldDeg, newDeg })
    }
  }
  return out
}

const KEY_SCALE_SPELLING = {
  C: ['C', 'D', 'E', 'F', 'G', 'A', 'B'],
  G: ['G', 'A', 'B', 'C', 'D', 'E', 'F#'],
  D: ['D', 'E', 'F#', 'G', 'A', 'B', 'C#'],
  A: ['A', 'B', 'C#', 'D', 'E', 'F#', 'G#'],
  E: ['E', 'F#', 'G#', 'A', 'B', 'C#', 'D#'],
  B: ['B', 'C#', 'D#', 'E', 'F#', 'G#', 'A#'],
  'F#': ['F#', 'G#', 'A#', 'B', 'C#', 'D#', 'E#'],
  F: ['F', 'G', 'A', 'Bb', 'C', 'D', 'E'],
  Bb: ['Bb', 'C', 'D', 'Eb', 'F', 'G', 'A'],
  Eb: ['Eb', 'F', 'G', 'Ab', 'Bb', 'C', 'D'],
  Ab: ['Ab', 'Bb', 'C', 'Db', 'Eb', 'F', 'G'],
  Db: ['Db', 'Eb', 'F', 'Gb', 'Ab', 'Bb', 'C'],
}

const MAJOR_STEPS = [0, 2, 4, 5, 7, 9, 11]
const MAJOR_DEGREE_QUALITY = ['maj', 'min', 'min', 'maj', 'maj', 'min', 'dim']
const ROMAN_TRIAD = ['I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°']

/** 도수 전이 (전형적인 조성 진행) */
const DEGREE_TRANSITIONS = {
  0: [3, 4, 5, 1, 2],
  1: [4, 6, 3],
  2: [5, 3],
  3: [4, 0, 1],
  4: [0, 5],
  5: [3, 1, 4],
  6: [0],
}

function randomChoice(arr) {
  if (!arr?.length) return null
  return arr[Math.floor(Math.random() * arr.length)]
}

/** 마디에 맞게 화음 슬롯 길이(4분음표 단위) 결정 */
export function chordSpanForMeter(baseRhythm, timeSignature) {
  const bpp = getBeatsPerMeasure(timeSignature)
  const want = harmonySpanBeats(baseRhythm)
  if (want <= 0) return 1
  if (want >= bpp) return bpp // 온음표 in 3/4 → 마디당 1화음
  if (bpp % want === 0) return want
  // 예: 8분 in 3/4 — 0.5가 나누면 OK; 아니면 1박 단위로 폴백
  if (Number.isInteger(bpp / want)) return want
  return 1
}

function durationTokenForBeats(beats) {
  const entries = Object.entries(BEAT_VALUES)
  const hit = entries.find(([, v]) => Math.abs(v - beats) < 1e-6)
  if (hit) return hit[0]
  // 못 맞추면 가장 가까운 표기 (드묾)
  let best = 'q'
  let bestDiff = Infinity
  for (const [k, v] of entries) {
    const d = Math.abs(v - beats)
    if (d < bestDiff) { bestDiff = d; best = k }
  }
  return best
}

function degreeChordTones(key, degree) {
  const spelling = KEY_SCALE_SPELLING[key]
  const tonicPc = KEY_TO_TONIC_PC[key]
  if (!spelling || tonicPc == null) return null
  const tones = {}
  for (const [role, off] of [['root', 0], ['third', 2], ['fifth', 4]]) {
    const d = (degree + off) % 7
    tones[role] = { letterAcc: spelling[d], pc: (tonicPc + MAJOR_STEPS[d]) % 12, degree: d }
  }
  // 7음 (속7/부7/감7)
  const sevDeg = (degree + 6) % 7
  tones.seventh = { letterAcc: spelling[sevDeg], pc: (tonicPc + MAJOR_STEPS[sevDeg]) % 12, degree: sevDeg }
  // 9음·11음 (속9·속11, 1편 6장)
  const ninthDeg = (degree + 1) % 7
  tones.ninth = { letterAcc: spelling[ninthDeg], pc: (tonicPc + MAJOR_STEPS[ninthDeg]) % 12, degree: ninthDeg }
  const eleventhDeg = (degree + 3) % 7
  tones.eleventh = { letterAcc: spelling[eleventhDeg], pc: (tonicPc + MAJOR_STEPS[eleventhDeg]) % 12, degree: eleventhDeg }
  return { quality: MAJOR_DEGREE_QUALITY[degree], tones }
}

const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B']
const LETTER_NATURAL_PC = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }

function letterAtOffset(letter, steps) {
  const idx = LETTERS.indexOf(letter)
  return LETTERS[((idx + steps) % 7 + 7) % 7]
}

/** 악보상 음이름(accidental 없는 letter)에 목표 pc를 맞추는 최소 accidental 계산 */
function toneForTargetPc(letter, targetPc) {
  const pc = ((targetPc % 12) + 12) % 12
  const naturalPc = LETTER_NATURAL_PC[letter]
  let diff = ((pc - naturalPc) % 12 + 12) % 12
  if (diff > 6) diff -= 12
  const accStr = diff === 0 ? '' : diff > 0 ? '#'.repeat(diff) : 'b'.repeat(-diff)
  return { letterAcc: `${letter}${accStr}`, pc }
}

/** 근음 letter·pc + 3도 간격(반음 수)으로 변화화음 tones 생성(온음계 밖 화음용) */
function buildTertianTones(rootLetter, rootPc, { third, fifth, seventh } = {}) {
  const tones = { root: toneForTargetPc(rootLetter, rootPc) }
  if (third != null) tones.third = toneForTargetPc(letterAtOffset(rootLetter, 2), rootPc + third)
  if (fifth != null) tones.fifth = toneForTargetPc(letterAtOffset(rootLetter, 4), rootPc + fifth)
  if (seventh != null) tones.seventh = toneForTargetPc(letterAtOffset(rootLetter, 6), rootPc + seventh)
  return tones
}

function raiseTone(tone, semitones) {
  return toneForTargetPc(tone.letterAcc[0], tone.pc + semitones)
}

/** 변성화음(모드믹스처, 2편 3장) 후보 — 장조에서 단조 차용. resolveDegree=자연스러운 해결처. */
function mixtureChordOptions(key) {
  const spelling = KEY_SCALE_SPELLING[key]
  const tonicPc = KEY_TO_TONIC_PC[key]
  return [
    { roman: 'iv', quality: 'min', resolveDegree: 0, tones: buildTertianTones(spelling[3][0], (tonicPc + 5) % 12, { third: 3, fifth: 7 }) },
    { roman: '♭VI', quality: 'maj', resolveDegree: 0, tones: buildTertianTones(spelling[5][0], (tonicPc + 8) % 12, { third: 4, fifth: 7 }) },
    { roman: '♭III', quality: 'maj', resolveDegree: 0, tones: buildTertianTones(spelling[2][0], (tonicPc + 3) % 12, { third: 4, fifth: 7 }) },
    { roman: 'ii°', quality: 'dim', resolveDegree: 4, tones: buildTertianTones(spelling[1][0], (tonicPc + 2) % 12, { third: 3, fifth: 6 }) },
  ]
}

/** 나폴리 6화음(2편 4장) — 1전위(N⁶/bII⁶)가 표준. */
function neapolitanChord(key, theoryBase) {
  const spelling = KEY_SCALE_SPELLING[key]
  const tonicPc = KEY_TO_TONIC_PC[key]
  const tones = buildTertianTones(spelling[1][0], (tonicPc + 1) % 12, { third: 4, fifth: 7 })
  const rules = getTheoryRules(theoryBase)
  const roman = rules?.neapolitan_symbol === 'N' ? 'N⁶' : 'bII⁶'
  return { roman, quality: 'maj', tones, bassRole: 'third' }
}

/** 증6화음(2편 5장) — It+6/Fr+6/Ger+6, 근위치. */
function aug6Chord(key, variant) {
  const spelling = KEY_SCALE_SPELLING[key]
  const tonicPc = KEY_TO_TONIC_PC[key]
  const root = toneForTargetPc(spelling[5][0], (tonicPc + 8) % 12) // ♭6
  const third = toneForTargetPc(spelling[0][0], tonicPc) // 1 (으뜸음)
  const fifth = toneForTargetPc(spelling[3][0], (tonicPc + 6) % 12) // ♯4
  const tones = { root, third, fifth }
  let roman = 'It.6'
  if (variant === 'fr') {
    tones.seventh = toneForTargetPc(spelling[1][0], (tonicPc + 2) % 12) // 2
    roman = 'Fr.6'
  } else if (variant === 'ger') {
    tones.seventh = toneForTargetPc(spelling[2][0], (tonicPc + 3) % 12) // ♭3
    roman = 'Ger.6'
  }
  return { roman, quality: 'aug6', tones, bassRole: 'root' }
}

const SECONDARY_TARGET_DEGREES = [1, 2, 3, 4, 5] // ii·iii·IV·V·vi (장조, 감3화음 제외)

/** V7/x — x의 완전5도 위(= x 자신의 딸림음)를 근음으로 하는 장단7화음(Mm7) */
function secondaryDominant7Tones(key, targetDegree) {
  const spelling = KEY_SCALE_SPELLING[key]
  const tonicPc = KEY_TO_TONIC_PC[key]
  const targetLetter = spelling[targetDegree][0]
  const targetPc = (tonicPc + MAJOR_STEPS[targetDegree]) % 12
  const rootLetter = letterAtOffset(targetLetter, 4) // 완전5도 위 = 알파벳 4걸음 위
  const rootPc = (targetPc + 7) % 12
  return buildTertianTones(rootLetter, rootPc, { third: 4, fifth: 7, seventh: 10 })
}

/** vii°7/x, viiø7/x — x의 반음 아래를 근음으로 하는 감7/반감7화음 */
function secondaryLeadingTone7Tones(key, targetDegree, kind) {
  const spelling = KEY_SCALE_SPELLING[key]
  const tonicPc = KEY_TO_TONIC_PC[key]
  const targetLetter = spelling[targetDegree][0]
  const targetPc = (tonicPc + MAJOR_STEPS[targetDegree]) % 12
  const rootLetter = letterAtOffset(targetLetter, -1)
  const rootPc = (targetPc - 1 + 12) % 12
  const seventh = kind === 'dim7' ? 9 : 10
  return buildTertianTones(rootLetter, rootPc, { third: 3, fifth: 6, seventh })
}

/**
 * 레벨에 따라 도수·화음 종류 선택.
 * @returns {{ degree: number, kind: string, roman: string, quality: string, bassRole: 'root'|'third'|'fifth',
 *   tonesOverride?: object, nextDegreeHint?: number, secondaryOf?: number }}
 */
function pickChord(prevDegree, unlocks, focus, slotIndex, totalSlots, key, theoryBase) {
  if (slotIndex === totalSlots - 1) {
    return {
      degree: 0,
      kind: 'triad',
      roman: 'I',
      quality: 'maj',
      bassRole: 'root',
    }
  }

  let degree
  if (slotIndex === 0) {
    degree = 0
  } else {
    const opts = DEGREE_TRANSITIONS[prevDegree] ?? [0, 3, 4]
    degree = randomChoice(opts)
  }

  // 종지 직전: V 쪽으로 (슬롯이 3개 이상일 때만 — 짧은 진행의 첫 화음을 건드리지 않음)
  if (totalSlots >= 3 && slotIndex === totalSlots - 2) {
    degree = 4
  }

  // 종지 전 예비마디(바로 앞 슬롯): 나폴리6 또는 증6화음으로 대체 (2편 4·5장)
  const predomGate = focus.has('aug6') || focus.has('neapolitan') ? 0.7 : 0.4
  if (
    totalSlots >= 4
    && slotIndex === totalSlots - 3
    && (unlocks.has('aug6') || unlocks.has('neapolitan'))
    && Math.random() < predomGate
  ) {
    // 이번 레벨 중심 소재 쪽을 우선 선택 (둘 다 해금됐고 어느 쪽도 중심이 아니면 반반)
    let aug6Weight = 0.5
    if (focus.has('aug6')) aug6Weight = 0.85
    else if (focus.has('neapolitan')) aug6Weight = 0.15
    const useAug6 = unlocks.has('aug6') && (!unlocks.has('neapolitan') || Math.random() < aug6Weight)
    if (useAug6) {
      const variant = randomChoice(['it', 'fr', 'ger'])
      const chord = aug6Chord(key, variant)
      return {
        degree: 4, kind: 'aug6', roman: chord.roman, quality: chord.quality,
        bassRole: chord.bassRole, tonesOverride: chord.tones, nextDegreeHint: 4,
      }
    }
    const chord = neapolitanChord(key, theoryBase)
    return {
      degree: 4, kind: 'neapolitan', roman: chord.roman, quality: chord.quality,
      bassRole: chord.bassRole, tonesOverride: chord.tones, nextDegreeHint: 4,
    }
  }

  // 변성화음(모드믹스처, 2편 3장) — 중간 슬롯에 차용화음 삽입
  if (
    unlocks.has('modeMixture')
    && slotIndex > 0
    && slotIndex < totalSlots - 2
    && Math.random() < chance('modeMixture', focus, 0.18, 0.4)
  ) {
    const chord = randomChoice(mixtureChordOptions(key))
    return {
      degree: chord.resolveDegree, kind: 'modeMixture', roman: chord.roman, quality: chord.quality,
      bassRole: 'root', tonesOverride: chord.tones, nextDegreeHint: chord.resolveDegree,
    }
  }

  let kind = 'triad'
  let roman = ROMAN_TRIAD[degree]
  const quality = MAJOR_DEGREE_QUALITY[degree]

  // V → V7 (레벨 3+) → V9 → V11 (레벨 6+) / 증속화음 V+ (2편 6장)
  const dom9or11Focus = focus.has('dom9') || focus.has('dom11')
  const augDomEligible = degree === 4 && slotIndex < totalSlots - 2 && unlocks.has('augDom')
  if (focus.has('augDom') && augDomEligible && Math.random() < 0.6) {
    // 증속화음이 이번 레벨 중심 소재면 V7 체인보다 먼저 시도(종지 직전 V와는 겹치지 않게)
    const base = degreeChordTones(key, 4).tones
    return {
      degree: 4, kind: 'augDom', roman: 'V+', quality: 'aug',
      bassRole: 'root', tonesOverride: { ...base, fifth: raiseTone(base.fifth, 1) },
    }
  }
  if (degree === 4 && unlocks.has('dom7') && Math.random() < chance('dom7', focus, 0.75, 0.9)) {
    kind = 'dom7'
    roman = 'V7'
    if (unlocks.has('dom9') && Math.random() < (dom9or11Focus ? 0.75 : 0.35)) {
      kind = 'dom9'
      roman = 'V9'
      if (unlocks.has('dom11') && Math.random() < (dom9or11Focus ? 0.6 : 0.3)) {
        kind = 'dom11'
        roman = 'V11'
      }
    }
  } else if (augDomEligible && Math.random() < chance('augDom', focus, 0.2, 0.45)) {
    // 증속화음 V+ (배경 확률, 위에서 focus로 이미 처리 안 된 경우)
    const base = degreeChordTones(key, 4).tones
    return {
      degree: 4, kind: 'augDom', roman: 'V+', quality: 'aug',
      bassRole: 'root', tonesOverride: { ...base, fifth: raiseTone(base.fifth, 1) },
    }
  }

  // vii° → vii°7 (레벨 4+)
  const dimFocus = focus.has('dim7') || focus.has('halfDim7')
  if (degree === 6 && unlocks.has('dim7') && Math.random() < (dimFocus ? 0.8 : 0.55)) {
    kind = 'dim7'
    roman = 'vii°7'
  } else if (degree === 6 && unlocks.has('halfDim7') && Math.random() < (dimFocus ? 0.6 : 0.35)) {
    kind = 'halfDim7'
    roman = 'viiø7'
  }

  // ii → ii7 (레벨 5+)
  if (degree === 1 && unlocks.has('diatonic7') && Math.random() < chance('diatonic7', focus, 0.45, 0.7)) {
    kind = 'diatonic7'
    roman = 'ii7'
  }

  // 부속화음: V7/x (레벨 2-1+), vii°7/x·viiø7/x (레벨 2-2+) — 다양한 도수를 토닉화
  if (
    unlocks.has('secondary')
    && slotIndex > 0
    && slotIndex < totalSlots - 2
    && Math.random() < chance('secondary', focus, 0.28, 0.5)
  ) {
    const targetDegree = randomChoice(SECONDARY_TARGET_DEGREES)
    if (unlocks.has('secondaryDim7') && Math.random() < chance('secondaryDim7', focus, 0.4, 0.65)) {
      const dimKind = Math.random() < 0.6 ? 'dim7' : 'halfDim7'
      const symbol = dimKind === 'dim7' ? 'vii°7' : 'viiø7'
      return {
        degree: targetDegree, kind: 'secondaryLT7', roman: `${symbol}/${ROMAN_TRIAD[targetDegree]}`,
        quality: dimKind, bassRole: 'root', secondaryOf: targetDegree,
        tonesOverride: secondaryLeadingTone7Tones(key, targetDegree, dimKind),
      }
    }
    return {
      degree: targetDegree, kind: 'secondaryDom7', roman: `V7/${ROMAN_TRIAD[targetDegree]}`,
      quality: 'dom7', bassRole: 'root', secondaryOf: targetDegree,
      tonesOverride: secondaryDominant7Tones(key, targetDegree),
    }
  }

  // 베이스: 대부분 근음. 경과적 1전위 (I–V⁶–I 등)
  let bassRole = 'root'
  if (kind === 'triad' && degree === 4 && Math.random() < 0.22) {
    bassRole = 'third' // V6
    roman = 'V⁶'
  } else if (kind === 'triad' && degree === 0 && slotIndex > 0 && Math.random() < 0.12) {
    bassRole = 'third' // I6
    roman = 'I⁶'
  } else if (kind === 'triad' && degree === 6) {
    bassRole = 'third' // vii°6 가 흔함
    roman = 'vii°⁶'
  } else if (kind === 'dom7' && Math.random() < 0.15) {
    bassRole = 'third' // V⁶₅
    roman = 'V⁶₅'
  }

  return { degree, kind, roman, quality, bassRole }
}

/** key·unlocks 고정 상태에서 slotCount개 슬롯을 순서대로 생성(전조 앞/뒤 구간 각각에 재사용). */
function buildSlotRun(key, theoryBase, unlocks, focus, slotCount, duration, span) {
  const slots = []
  let prevDegree = 0
  for (let i = 0; i < slotCount; i++) {
    const pick = pickChord(prevDegree, unlocks, focus, i, slotCount, key, theoryBase)
    const tones = pick.tonesOverride || degreeChordTones(key, pick.degree).tones
    const bassTone = tones[pick.bassRole] || tones.root
    slots.push({
      index: i,
      duration,
      spanBeats: span,
      degree: pick.degree,
      kind: pick.kind,
      roman: pick.roman,
      quality: pick.quality,
      bassRole: pick.bassRole,
      bassLetter: bassTone.letterAcc,
      bassPc: bassTone.pc,
      secondaryOf: pick.secondaryOf ?? null,
      tones,
    })
    prevDegree = pick.nextDegreeHint ?? pick.degree
  }

  // 부속화음 다음이 토닉화 대상이 아니면 다음 슬롯을 그 대상(온음계 3화음)으로 보정 (종지 직전 제외 시).
  // 다음 슬롯이 같은 목표를 향한 또 다른 부속화음(우연히 중복 선택됨)인 경우도 — 화면에
  // 로마숫자가 중복 억제로 비어 보이는 문제가 생기므로 — 즉시 해결시킨다.
  for (let i = 0; i < slots.length - 1; i++) {
    const isSecondary = slots[i].kind === 'secondaryDom7' || slots[i].kind === 'secondaryLT7'
    const target = slots[i].secondaryOf
    const nextIsSameSecondary =
      (slots[i + 1].kind === 'secondaryDom7' || slots[i + 1].kind === 'secondaryLT7')
      && slots[i + 1].secondaryOf === target
    if (isSecondary && target != null && (slots[i + 1].degree !== target || nextIsSameSecondary)) {
      const targetTones = degreeChordTones(key, target).tones
      slots[i + 1] = {
        ...slots[i + 1],
        degree: target,
        kind: 'triad',
        roman: ROMAN_TRIAD[target],
        quality: MAJOR_DEGREE_QUALITY[target],
        bassRole: 'root',
        bassLetter: targetTones.root.letterAcc,
        bassPc: targetTones.root.pc,
        tones: targetTones,
      }
    }
  }

  return slots
}

/**
 * 온음계적 전조(공통화음, 1편 8장 §1) — 원조 구간(V-I 종지로 원조 확립) → 공통화음(피벗,
 * 두 조 겸용 표기) → 새 조로 넘어가는 확정화음("^" 이음줄 표시) → 새 조 구간(V-I 종지로 확정).
 * 이론상 요구되는 "최소 세 번의 종지" 중 원조 종지·새 조 종지 두 번을 실제로 만족시킨다.
 */
function buildDiatonicModulationSlots(key, newKey, theoryBase, unlocks, focus, slotCount, duration, span) {
  const candidates = pivotChordCandidates(key, newKey)
  if (candidates.length === 0) return null

  const firstCount = Math.max(3, Math.floor(slotCount * 0.4))
  const restCount = slotCount - firstCount - 1
  if (restCount < 3) return null

  // 원조 마지막 화음(I)에서 자연스럽게 이어지는 피벗 쪽을 우선 (I → IV/V/vi/ii/iii).
  // 새 조 쪽은 으뜸화음(I) 재해석은 제외 — 원서 예시(C의 I = G의 IV)처럼 피벗 자체가
  // 새 조의 종속적 화음으로 작용해야 바로 뒤 확정화음("^")과 구별된다.
  const reachable = candidates.filter((c) => (DEGREE_TRANSITIONS[0] ?? []).includes(c.oldDeg))
  const nonTonicNew = (reachable.length > 0 ? reachable : candidates).filter((c) => c.newDeg !== 0)
  const pivotPool = nonTonicNew.length > 0 ? nonTonicNew : (reachable.length > 0 ? reachable : candidates)
  const pivot = randomChoice(pivotPool)

  const firstPart = buildSlotRun(key, theoryBase, unlocks, focus, firstCount, duration, span)

  const pivotTones = degreeChordTones(key, pivot.oldDeg).tones
  const oldRoman = ROMAN_TRIAD[pivot.oldDeg]
  const newRoman = ROMAN_TRIAD[pivot.newDeg]
  const pivotSlot = {
    index: 0,
    duration,
    spanBeats: span,
    degree: pivot.oldDeg,
    kind: 'triad',
    roman: `${oldRoman}\n${newKey}: ${newRoman}`,
    quality: MAJOR_DEGREE_QUALITY[pivot.oldDeg],
    bassRole: 'root',
    bassLetter: pivotTones.root.letterAcc,
    bassPc: pivotTones.root.pc,
    secondaryOf: null,
    tones: pivotTones,
    modulation: newKey,
  }

  const secondPart = buildSlotRun(newKey, theoryBase, unlocks, focus, restCount, duration, span)
  secondPart[0] = { ...secondPart[0], roman: `^${secondPart[0].roman}` }

  return [firstPart, [pivotSlot], secondPart].flat().map((s, idx) => ({ ...s, index: idx }))
}

/**
 * 반음계적 전조(1편 8장 §2) — 공통화음 없이, 원조의 I에서 새 조의 부속화음(V7/새조)으로
 * 직접 진행한 뒤 바로 새 조의 I로 해결한다("V7/Eb →" 한 줄 + 화살표 표기, 이음줄 없음).
 */
function buildChromaticModulationSlots(key, newKey, theoryBase, unlocks, focus, slotCount, duration, span) {
  const firstCount = Math.max(1, Math.floor(slotCount * 0.4))
  const restCount = slotCount - firstCount - 1
  if (restCount < 3) return null

  const firstPart = buildSlotRun(key, theoryBase, unlocks, focus, firstCount, duration, span)

  const chromTones = degreeChordTones(newKey, 4).tones
  const chromSlot = {
    index: 0,
    duration,
    spanBeats: span,
    degree: 4,
    kind: 'dom7',
    roman: `V7/${newKey} →`,
    quality: 'dom7',
    bassRole: 'root',
    bassLetter: chromTones.root.letterAcc,
    bassPc: chromTones.root.pc,
    secondaryOf: null,
    tones: chromTones,
    modulation: newKey,
  }

  const secondPart = buildSlotRun(newKey, theoryBase, unlocks, focus, restCount, duration, span)

  return [firstPart, [chromSlot], secondPart].flat().map((s, idx) => ({ ...s, index: idx }))
}

/**
 * @param {object} opts
 * @returns {{
 *   slots: Array<object>,
 *   romanSummary: string,
 *   chordSpanBeats: number,
 *   duration: string,
 * }}
 */
export function generateHarmonyProgression({
  key = 'C',
  timeSignature = '4/4',
  measureCount = 4,
  baseRhythm = 'q',
  solveLevel = DEFAULT_SOLVE_LEVEL,
  theoryBase = 'baek',
}) {
  if (!KEY_SCALE_SPELLING[key]) {
    throw new Error(`지원하지 않는 조: ${key}`)
  }
  const levelId = normalizeSolveLevelId(solveLevel)
  const unlocks = unlocksUpToLevel(levelId)
  const focus = focusFamiliesForLevel(levelId)
  const bpp = getBeatsPerMeasure(timeSignature)
  const span = chordSpanForMeter(baseRhythm, timeSignature)
  const totalBeats = Math.max(1, measureCount) * bpp
  const slotCount = Math.max(1, Math.round(totalBeats / span))
  const duration = durationTokenForBeats(span)

  let slots = null
  let modulatedTo = null
  const modGate = chance('modulation', focus, 0.3, 0.85)
  if (unlocks.has('modulation') && slotCount >= 7 && Math.random() < modGate) {
    // 전조(1편 8장) — 온음계적(공통화음) 전조와 반음계적 전조 중 하나를 고른다.
    const targetKey = Math.random() < 0.5 ? dominantKeyOf(key) : subdominantKeyOf(key)
    const useDiatonic = Math.random() < 0.5
    slots = useDiatonic
      ? buildDiatonicModulationSlots(key, targetKey, theoryBase, unlocks, focus, slotCount, duration, span)
      : buildChromaticModulationSlots(key, targetKey, theoryBase, unlocks, focus, slotCount, duration, span)
    if (slots) modulatedTo = targetKey
  }
  if (!slots) {
    slots = buildSlotRun(key, theoryBase, unlocks, focus, slotCount, duration, span)
  }

  return {
    slots,
    romanSummary: slots.map((s) => s.roman).join(' – '),
    chordSpanBeats: span,
    duration,
    key,
    timeSignature,
    measureCount,
    solveLevel: levelId,
    baseRhythm,
    modulatedTo,
  }
}

function pitchOptionsForLetter(letterAcc, range) {
  const out = []
  for (let oct = 0; oct <= 8; oct++) {
    const pitch = `${letterAcc}${oct}`
    const midi = pitchToMidi(pitch)
    if (midi == null) continue
    if (midi >= range.minMidi && midi <= range.maxMidi) out.push({ pitch, midi })
  }
  return out
}

/**
 * 베이스 옥타브 선택: "최근접"만 쓰면 A3→D4처럼 가성대에 붙는다.
 * 낮은 음역·작은 도약·하행 선호 점수로 고른다.
 */
function pickBassPitch(options, prevMidi) {
  // 생성용 편안 상한(C4). 이론상 E4까지 가능해도 문제용 베이스는 C4 이하를 강하게 선호.
  const comfortMax = 60
  const comfortCenter = 48 // C3
  const softHigh = 57 // A3 이상 soft penalty
  const preferredLeap = 7 // 완전5도

  const scored = options.map((o) => {
    let score = 0
    if (prevMidi == null) {
      score += Math.abs(o.midi - comfortCenter) * 3
    } else {
      const leap = Math.abs(o.midi - prevMidi)
      score += leap
      if (leap > preferredLeap) score += (leap - preferredLeap) * 4
      // 동률에 가깝면 상행보다 하행
      if (o.midi > prevMidi) score += 1.2
    }
    if (o.midi > comfortMax) score += (o.midi - comfortMax) * 10
    else if (o.midi > softHigh) score += (o.midi - softHigh) * 2.5
    score += Math.abs(o.midi - comfortCenter) * 0.2
    return { ...o, score }
  })
  scored.sort((a, b) => a.score - b.score || a.midi - b.midi)

  // 아주 가까운 2등만 가끔 (큰 고음 도약은 절대 허용하지 않음)
  if (
    scored.length > 1
    && Math.random() < 0.1
    && scored[1].score - scored[0].score < 2.5
    && scored[1].midi <= comfortMax
  ) {
    return scored[1]
  }
  return scored[0]
}

function diatonicPcSet(key) {
  const tonicPc = KEY_TO_TONIC_PC[key]
  return new Set(MAJOR_STEPS.map((s) => (tonicPc + s) % 12))
}

/**
 * 경과음 삽입(비화성음, 1편 7장) — 화성(slots)은 그대로 두고, 3도 간격으로 움직이는
 * 음표를 절반 길이 둘로 쪼개 그 사이 온음계 순차음 하나를 끼워 넣는다.
 */
function applyPassingTones(notes, key, insertChance = 0.3) {
  const scalePcs = diatonicPcSet(key)
  const out = []
  for (let i = 0; i < notes.length; i++) {
    const cur = notes[i]
    const next = notes[i + 1]
    const curBeats = BEAT_VALUES[cur?.duration] ?? 0
    if (cur?.pitch && next?.pitch && !cur.isRest && !next.isRest && curBeats >= 1 && Math.random() < insertChance) {
      const curMidi = pitchToMidi(cur.pitch)
      const nextMidi = pitchToMidi(next.pitch)
      const diff = nextMidi - curMidi
      if (Math.abs(diff) === 3 || Math.abs(diff) === 4) {
        const step = diff > 0 ? 1 : -1
        let passMidi = null
        for (let m = curMidi + step; m !== nextMidi; m += step) {
          if (scalePcs.has(((m % 12) + 12) % 12)) { passMidi = m; break }
        }
        if (passMidi != null) {
          const halfDur = durationTokenForBeats(curBeats / 2)
          out.push({ pitch: cur.pitch, duration: halfDur })
          out.push({ pitch: midiToPitch(passMidi, { keySignature: key }), duration: halfDur, isNCT: true })
          continue
        }
      }
    }
    out.push(cur)
  }
  return out
}

/**
 * 화성진행 → 베이스 노트열 (주로 근음, 낮은 음역·순차 선호).
 * @returns {Array<{pitch:string, duration:string}>}
 */
export function bassNotesFromProgression(progression, { theoryBase = 'baek' } = {}) {
  const range = getVoiceRanges(theoryBase).bass
  const notes = []
  let prevMidi = null

  for (const slot of progression.slots) {
    const options = pitchOptionsForLetter(slot.bassLetter, range)
    if (!options.length) {
      notes.push({ isRest: true, duration: slot.duration })
      continue
    }
    const chosen = pickBassPitch(options, prevMidi)
    notes.push({ pitch: chosen.pitch, duration: slot.duration })
    prevMidi = chosen.midi
  }
  const unlocks = unlocksUpToLevel(progression.solveLevel)
  const focus = focusFamiliesForLevel(progression.solveLevel)
  return unlocks.has('nct') ? applyPassingTones(notes, progression.key, chance('nct', focus, 0.3, 0.55)) : notes
}

function sopranoRolesForSlot(slot, isLast) {
  if (isLast) {
    // 종지: 으뜸음(근음) 또는 3음
    return Math.random() < 0.65 ? ['root', 'third'] : ['third', 'root']
  }
  const roles = ['third', 'fifth', 'root']
  const sevenTone = ['dom7', 'diatonic7', 'dim7', 'halfDim7', 'secondaryDom7', 'secondaryLT7', 'aug6', 'dom9', 'dom11']
  if (sevenTone.includes(slot.kind)) {
    if (Math.random() < 0.25) roles.unshift('seventh')
  }
  if (slot.kind === 'dom9' || slot.kind === 'dom11') {
    if (Math.random() < 0.3) roles.unshift('ninth')
  }
  if (slot.kind === 'dom11' && Math.random() < 0.25) roles.unshift('eleventh')
  return roles
}

function uniquePitchOptions(letterList, range) {
  const seen = new Set()
  const out = []
  for (const letterAcc of letterList) {
    if (!letterAcc) continue
    for (const o of pitchOptionsForLetter(letterAcc, range)) {
      if (seen.has(o.midi)) continue
      seen.add(o.midi)
      out.push(o)
    }
  }
  return out
}

function sopranoOptionsForSlot(slot, isLast, range) {
  const roleOrder = sopranoRolesForSlot(slot, isLast)
  const letters = []
  for (const role of roleOrder) {
    const tone = slot.tones?.[role]
    if (tone?.letterAcc) letters.push(tone.letterAcc)
  }
  return uniquePitchOptions(letters, range)
}

/**
 * 화성진행 → 소프라노 선율 (화음음 위주, 순차·단일 정점 경향).
 */
export function sopranoNotesFromProgression(progression, { theoryBase = 'baek' } = {}) {
  const range = getVoiceRanges(theoryBase).soprano
  const notes = []
  let prevMidi = null
  const n = progression.slots.length

  for (let i = 0; i < n; i++) {
    const slot = progression.slots[i]
    const options = sopranoOptionsForSlot(slot, i === n - 1, range)
    if (!options.length) {
      notes.push({ isRest: true, duration: slot.duration })
      continue
    }

    let chosen
    if (prevMidi == null) {
      const mid = (range.minMidi + range.maxMidi) / 2 + 2
      chosen = [...options].sort((a, b) => Math.abs(a.midi - mid) - Math.abs(b.midi - mid))[0]
    } else {
      const sorted = [...options].sort((a, b) => Math.abs(a.midi - prevMidi) - Math.abs(b.midi - prevMidi))
      const pickN = Math.min(3, sorted.length)
      chosen = Math.random() < 0.72 ? sorted[0] : sorted[Math.floor(Math.random() * pickN)]
    }
    notes.push({ pitch: chosen.pitch, duration: slot.duration })
    prevMidi = chosen.midi
  }
  const unlocks = unlocksUpToLevel(progression.solveLevel)
  const focus = focusFamiliesForLevel(progression.solveLevel)
  return unlocks.has('nct') ? applyPassingTones(notes, progression.key, chance('nct', focus, 0.3, 0.55)) : notes
}

/** V(7) → I 정격종지 슬롯인지 */
function isAuthenticCadenceTransition(prevSlot, currSlot) {
  if (!prevSlot || !currSlot) return false
  const prevIsV = prevSlot.degree === 4
    || prevSlot.kind === 'dom7'
    || /^V/.test(String(prevSlot.roman || ''))
  const currIsI = currSlot.degree === 0 && (currSlot.kind === 'triad' || !currSlot.kind)
  return Boolean(prevIsV && currIsI)
}

/**
 * 외성(S–B) 경성 위반. harmonyEngine parallel / hidden / convergence / unequal5 와 동일 기준.
 * @returns {string[]} 비어 있으면 통과
 */
export function outerVoiceHardViolations(prevS, prevB, currS, currB, {
  theoryBase = 'baek',
  isAuthenticCadence = false,
} = {}) {
  const issues = []
  if (currS == null || currB == null) return ['missing']
  if (currS <= currB) issues.push('crossing')

  if (prevS == null || prevB == null) return issues

  const rules = getTheoryRules(theoryBase)
  const dS = currS - prevS
  const dB = currB - prevB
  const intP = Math.abs(prevS - prevB) % 12
  const intC = Math.abs(currS - currB) % 12
  const bothMove = dS !== 0 && dB !== 0
  const sameDir = bothMove && (dS > 0) === (dB > 0)
  const contrary = bothMove && (dS > 0) !== (dB > 0)

  if ([0, 7].includes(intP) && [0, 7].includes(intC) && intP === intC && bothMove) {
    if (sameDir) {
      issues.push('parallel')
    } else if (contrary) {
      const allowOct = rules.allow_cadential_contrary_parallel_octave
        && isAuthenticCadence
        && intP === 0 && intC === 0
      if (!allowOct) issues.push('contrary-parallel')
    }
  }

  if (sameDir && [0, 7].includes(intC) && intP !== intC && Math.abs(dS) >= 3) {
    issues.push('hidden-parallel')
  }

  if (rules.check_outer_voice_convergence && contrary && prevS !== prevB && currS === currB) {
    issues.push('convergence')
  }

  if (sameDir && intP === 6 && intC === 7) {
    issues.push('unequal-fifths')
  }

  return issues
}

function scoreOuterPair(prevS, prevB, sMidi, bMidi) {
  let score = 0
  if (prevS != null) score += Math.abs(sMidi - prevS)
  if (prevB != null) {
    const leap = Math.abs(bMidi - prevB)
    score += leap
    if (leap > 7) score += (leap - 7) * 4
    if (bMidi > prevB) score += 1.2
  } else {
    score += Math.abs(bMidi - 48) * 0.35
  }
  if (bMidi > 60) score += (bMidi - 60) * 10
  else if (bMidi > 57) score += (bMidi - 57) * 2.5
  score += Math.abs(sMidi - 69) * 0.15
  if (sMidi - bMidi > 36) score += (sMidi - bMidi - 36) * 0.5
  if (prevS != null && prevB != null) {
    const dS = sMidi - prevS
    const dB = bMidi - prevB
    if (dS !== 0 && dB !== 0 && (dS > 0) === (dB > 0)) score += 2.8
    if (dS !== 0 && dB !== 0 && (dS > 0) !== (dB > 0)) score -= 0.9
  }
  return score
}

function pickJointOuterPair(bassOpts, sopOpts, prevS, prevB, {
  theoryBase,
  isAuthenticCadence,
}) {
  const candidates = []
  for (const b of bassOpts) {
    for (const s of sopOpts) {
      const viol = outerVoiceHardViolations(prevS, prevB, s.midi, b.midi, {
        theoryBase,
        isAuthenticCadence,
      })
      if (viol.length) continue
      candidates.push({
        s,
        b,
        score: scoreOuterPair(prevS, prevB, s.midi, b.midi),
      })
    }
  }
  if (candidates.length) {
    candidates.sort((a, b) => a.score - b.score || a.b.midi - b.b.midi)
    const best = candidates[0].score
    const near = candidates.filter((c) => c.score <= best + 2.2).slice(0, 8)
    return near[Math.floor(Math.random() * near.length)]
  }

  // 폴백: 교차만 피하고 경성 성부법을 잠시 완화 (이론상 드묾)
  const soft = []
  for (const b of bassOpts) {
    for (const s of sopOpts) {
      if (s.midi <= b.midi) continue
      soft.push({
        s,
        b,
        score: scoreOuterPair(prevS, prevB, s.midi, b.midi) + 20,
      })
    }
  }
  if (soft.length) {
    soft.sort((a, b) => a.score - b.score)
    return soft[0]
  }
  return null
}

/**
 * 진행 슬롯 → ScoreRenderer용 analysisResult (박 중앙 샘플에 화성 기호).
 */
export function analysisResultFromProgression(progression) {
  const bpp = getBeatsPerMeasure(progression.timeSignature || '4/4')
  const span = progression.chordSpanBeats || 1
  const measureCount = progression.measureCount || 1
  const slots = progression.slots || []
  const measures = []

  for (let m = 0; m < measureCount; m++) {
    const beats = []
    for (let b = 0; b < bpp; b++) {
      const tCenter = m * bpp + b + 0.5
      const slotIndex = Math.min(slots.length - 1, Math.floor(tCenter / span))
      const slot = slots[Math.max(0, slotIndex)]
      beats.push({
        beat: b + 1,
        chordSymbol: slot?.roman || '—',
        inversion: '—',
        voiceLeadingIssues: [],
        analyzedBy: 'solve-progression',
      })
    }
    measures.push({
      measure: m + 1,
      beats,
      chordSymbol: beats[0]?.chordSymbol ?? '—',
      inversion: '—',
      analyzedBy: 'solve-progression',
      voiceLeadingIssues: [],
      tonicization: null,
    })
  }

  return {
    measures,
    theoryBase: progression.solveLevel != null ? `solve-${progression.solveLevel}` : 'solve',
    source: 'harmonyProgression',
  }
}

/**
 * @param {'soprano'|'bass'} voice
 */
export function generateVoiceWithProgression(voice, opts) {
  if (voice !== 'soprano' && voice !== 'bass') {
    throw new Error("voice는 'soprano' 또는 'bass'여야 합니다.")
  }
  const progression = generateHarmonyProgression(opts)
  const theoryBase = opts.theoryBase || 'baek'
  const notes = voice === 'bass'
    ? bassNotesFromProgression(progression, { theoryBase })
    : sopranoNotesFromProgression(progression, { theoryBase })
  const analysisResult = analysisResultFromProgression(progression)
  return { progression, notes, analysisResult }
}

/**
 * 같은 화성진행으로 소프라노·베이스를 함께 생성.
 * 슬롯마다 외성부 성부법(병행5·8, 은복, 유니즌 수렴, 교차, 감5→완5)을 만족하는 쌍만 고른다.
 */
export function generateOuterVoicesWithProgression(opts) {
  const progression = generateHarmonyProgression(opts)
  const theoryBase = opts.theoryBase || 'baek'
  const ranges = getVoiceRanges(theoryBase)
  const slots = progression.slots || []
  const soprano = []
  const bass = []
  let prevS = null
  let prevB = null

  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i]
    const isLast = i === slots.length - 1
    const isCadence = i > 0 && isAuthenticCadenceTransition(slots[i - 1], slot)
    const bassOpts = pitchOptionsForLetter(slot.bassLetter, ranges.bass)
    const sopOpts = sopranoOptionsForSlot(slot, isLast, ranges.soprano)

    if (!bassOpts.length || !sopOpts.length) {
      soprano.push({ isRest: true, duration: slot.duration })
      bass.push({ isRest: true, duration: slot.duration })
      continue
    }

    const chosen = pickJointOuterPair(bassOpts, sopOpts, prevS, prevB, {
      theoryBase,
      isAuthenticCadence: isCadence,
    })
    if (!chosen) {
      soprano.push({ isRest: true, duration: slot.duration })
      bass.push({ isRest: true, duration: slot.duration })
      continue
    }

    soprano.push({ pitch: chosen.s.pitch, duration: slot.duration })
    bass.push({ pitch: chosen.b.pitch, duration: slot.duration })
    prevS = chosen.s.midi
    prevB = chosen.b.midi
  }

  const unlocks = unlocksUpToLevel(progression.solveLevel)
  const focus = focusFamiliesForLevel(progression.solveLevel)
  return {
    progression,
    soprano: unlocks.has('nct') ? applyPassingTones(soprano, progression.key, chance('nct', focus, 0.3, 0.55)) : soprano,
    bass,
    analysisResult: analysisResultFromProgression(progression),
  }
}

/** @deprecated generateVoiceWithProgression('bass', opts) 사용 */
export function generateBassWithProgression(opts) {
  return generateVoiceWithProgression('bass', opts)
}

